/**
 * Segment workbench (variant 6): CMS — global lead strip (outside tabs), then tabs:
 * phone/OTP, primary intake, secondary, completion, returning.
 */

const json = (field: Record<string, unknown>) => ({ ...field, isJsonField: true as const });

/**
 * Above segment tabs — shared headline + backdrop for every intake step (distinct from step section titles/subtitles).
 * Persisted keys; runtime also falls back to legacy `title`, `form_title`, `banner_gradient_*`, etc.
 */
export const LEAD_STRIP_GLOBAL_FIELDS = [
  json({
    fieldName: "lead_strip_title",
    label: "Lead title (banner on every intake step)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "e.g. Admission Closed on 02 Feb",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "lead_strip_title_color",
    label: "Lead title color",
    value: "#FFFFFF",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#FFFFFF",
  }),
  json({
    fieldName: "lead_gradient_angle",
    label: "Background gradient angle",
    value: "90deg",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "90deg",
    required: false,
    defaultValue: "90deg",
  }),
  json({
    fieldName: "lead_gradient_start",
    label: "Background gradient start",
    value: "#03a4b5",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#03a4b5",
  }),
  json({
    fieldName: "lead_gradient_end",
    label: "Background gradient end",
    value: "#342b7c",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#342b7c",
  }),
];

/**
 * University lead drop + page “Mobile lead form 6” only — compact strip CTA outside the popup.
 * Omit from CMS for lead popup variant 6.
 */
export const MOBILE_STRIP_LEADDROP_CTA_FIELDS = [
  json({
    fieldName: "mobile_cta_text",
    label: "[Mobile strip] CTA text",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Apply Now",
    required: false,
    defaultValue: "Apply Now",
  }),
  json({
    fieldName: "mobile_cta_button_color",
    label: "[Mobile strip] CTA background",
    value: "#FFFFFF",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#FFFFFF",
  }),
  json({
    fieldName: "mobile_cta_button_text_color",
    label: "[Mobile strip] CTA text color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
];

/** Helper copy shown only when {@link MOBILE_STRIP_LEADDROP_CTA_FIELDS} are in CMS (lead drop / page mobile, not popup). */
export const LEADDROP_MOBILE_STRIP_HELPER_TEXT =
  "On smaller screens the full intake is not embedded in the page: visitors only see this lead headline and button row. Tapping the button opens the full multi-step intake.";

/** Phone & OTP — before step 1 (hero, mobile label, OTP labels, buttons, consent) */
const PHONE_INTAKE_FIELDS = [
  json({
    fieldName: "seg0_hero_title",
    label: "Phone screen Title",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "e.g. Adapt Early or Fall Behind",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg0_hero_title_color",
    label: "Phone screen Title color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "#222222",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg0_hero_subtitle",
    label: "Phone screen Subtitle", 
    value: "",
    valueType: "string",
    filedType: "TEXT_AREA",
    placeholder: "",
    required: false,
    defaultValue: "",
    rows: 2,
  }),
  json({
    fieldName: "seg0_hero_subtitle_color",
    label: "Phone screen Subtitle color",
    value: "rgba(34,34,34,0.85)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(34,34,34,0.85)",
  }),
  json({
    fieldName: "seg0_mobile_number_label",
    label: "Mobile number — label",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Phone No.",
    required: false,
    defaultValue: "Phone No.",
  }),
  json({
    fieldName: "seg0_mobile_number_label_color",
    label: "Mobile number — label color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "#222222",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg0_country_code_text_color",
    label: "Country code (+91) text color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg0_input_border_color",
    label: "Phone / OTP input border color",
    value: "rgba(196,196,196,0.4)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(196,196,196,0.4)",
  }),
  json({
    fieldName: "seg0_input_background_color",
    label: "Phone / OTP input background",
    value: "rgba(196,196,196,0.2)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(196,196,196,0.2)",
  }),
  json({
    fieldName: "seg0_input_text_color",
    label: "Phone / OTP input text color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg0_input_placeholder_color",
    label: "Phone / OTP placeholder color",
    value: "rgba(34,34,34,0.7)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(34,34,34,0.7)",
  }),
  json({
    fieldName: "seg0_get_otp_button",
    label: "Get OTP — button text",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Get OTP",
    required: false,
    defaultValue: "Get OTP",
  }),
  json({
    fieldName: "seg0_get_otp_button_text_color",
    label: "Get OTP — button text color",
    value: "#FFFFFF",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#FFFFFF",
  }),
  json({
    fieldName: "seg0_get_otp_button_background_color",
    label: "Get OTP — button background",
    value: "#02a7b6",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#02a7b6",
  }),
  json({
    fieldName: "seg0_consent_text",
    label: "Consent / terms text",
    value: "",
    valueType: "string",
    filedType: "TEXT_AREA",
    placeholder: "",
    required: false,
    defaultValue: "",
    rows: 3,
  }),
  json({
    fieldName: "seg0_consent_text_color",
    label: "Consent text color",
    value: "#272323",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#272323",
  }),
  json({
    fieldName: "seg0_checkbox_color",
    label: "Consent checkbox accent color",
    value: "#02a7b6",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#02a7b6",
  }),
  json({
    fieldName: "seg0_otp_screen_title",
    label: "OTP step — headline",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "Enter Verification Code",
  }),
  json({
    fieldName: "seg0_otp_screen_title_color",
    label: "OTP step — headline color",
    value: "#111827",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#111827",
  }),
  json({
    fieldName: "seg0_otp_sent_line_template",
    label: 'OTP step — sent line (use {{mask}} for masked mobile, e.g. "XXXXXXX123")',
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg0_otp_sent_line_color",
    label: "OTP step — sent line text color",
    value: "#6B7280",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#6B7280",
  }),
  json({
    fieldName: "seg0_otp_change_number_text",
    label: "OTP step — Change number link",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "Change Number",
  }),
  json({
    fieldName: "seg0_otp_input_heading",
    label: "OTP step — 4-digit field label",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "Enter 4-digit OTP Here",
  }),
  json({
    fieldName: "seg0_otp_input_heading_color",
    label: "OTP step — 4-digit label color",
    value: "#111827",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#111827",
  }),
  json({
    fieldName: "seg0_otp_resend_prefix",
    label: "OTP step — resend row (prefix, before link)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "Didn't receive the OTP?",
  }),
  json({
    fieldName: "seg0_otp_resend_link_text",
    label: "OTP step — resend link text",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "Resend OTP",
  }),
  json({
    fieldName: "seg0_verify_button_text",
    label: "Verify — button text",
    value: "Verify",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Verify",
    required: false,
    defaultValue: "Verify",
  }),
  json({
    fieldName: "seg0_verify_button_text_color",
    label: "Verify — button text color",
    value: "#FFFFFF",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#FFFFFF",
  }),
  json({
    fieldName: "seg0_verify_button_background_color",
    label: "Verify — button background",
    value: "#02a7b6",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#02a7b6",
  }),
];

/** Step 1 — user details (after OTP) */
const PRIMARY_INTAKE_FIELDS = [
  json({
    fieldName: "seg1_step1_section_title",
    label:
      "Step indicator — Step 1 label (beside circle; shown next to Step 1 when active, hidden when Step 2 is active)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "Your details",
  }),
  json({
    fieldName: "seg1_phase_hero_title",
    label: "Step 1 — Main headline (white card)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "Adapt Early or Fall Behind",
  }),
  json({
    fieldName: "seg1_phase_hero_subtitle",
    label: "Step 1 — Subtitle below headline",
    value: "",
    valueType: "string",
    filedType: "TEXT_AREA",
    placeholder:
      "Shown under the headline for Step 1. Falls back to phone-step hero subtitle if unset.",
    required: false,
    defaultValue: "60% of business tasks are becoming AI-driven, stay relevant!",
    rows: 2,
  }),
  json({
    fieldName: "seg1_phase_hero_title_color",
    label: "Step 1 — Headline color",
    value: "#111827",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#111827",
  }),
  json({
    fieldName: "seg1_phase_hero_subtitle_color",
    label: "Step 1 — Subtitle color",
    value: "#6B7280",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#6B7280",
  }),
  json({
    fieldName: "seg1_full_name_label",
    label: "Full name label",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Full name",
    required: false,
    defaultValue: "Full name",
  }),
  json({
    fieldName: "seg1_full_name_label_color",
    label: "Full name label color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg1_full_name_placeholder",
    label: "Full name — text input placeholder (empty field)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "e.g. Enter your full name",
    required: false,
    defaultValue: "Enter your full name",
  }),
  json({
    fieldName: "seg1_email_label",
    label: "Email label",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Email",
    required: false,
    defaultValue: "Email",
  }),
  json({
    fieldName: "seg1_email_label_color",
    label: "Email label color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg1_email_placeholder",
    label: "Email — text input placeholder (empty field)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "e.g. name@example.com",
    required: false,
    defaultValue: "Enter your email address",
  }),
  json({
    fieldName: "seg1_qualification_label",
    label: "Qualification label",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Qualification",
    required: false,
    defaultValue: "Qualification",
  }),
  json({
    fieldName: "seg1_qualification_label_color",
    label: "Qualification label color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg1_qualification_placeholder",
    label:
      "Qualification — dropdown placeholder (options come from Admin → Higher Qualification / university API, same as Lead Form 2)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "Enter Highest Qualifications",
  }),
  json({
    fieldName: "seg1_program_interest_label",
    label: "Program / interest label",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Program / interest",
    required: false,
    defaultValue: "Program / interest",
  }),
  json({
    fieldName: "seg1_program_interest_label_color",
    label: "Program / interest label color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg1_program_interest_placeholder",
    label:
      "Program / interest — dropdown placeholder (courses come from university courses API, same as Lead Form 2)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "Enter Area of Interest",
  }),
  json({
    fieldName: "seg1_continue_button_text",
    label: "Continue — button text",
    value: "Continue",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Continue",
    required: false,
    defaultValue: "Continue",
  }),
  json({
    fieldName: "seg1_continue_button_text_color",
    label: "Continue — button text color",
    value: "#FFFFFF",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#FFFFFF",
  }),
  json({
    fieldName: "seg1_continue_button_background_color",
    label: "Continue — button background",
    value: "#02a7b6",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#02a7b6",
  }),
  json({
    fieldName: "seg1_input_border_color",
    label: "Step 1 input border color",
    value: "rgba(196,196,196,0.4)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(196,196,196,0.4)",
  }),
  json({
    fieldName: "seg1_input_background_color",
    label: "Step 1 input background",
    value: "rgba(196,196,196,0.2)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(196,196,196,0.2)",
  }),
  json({
    fieldName: "seg1_input_text_color",
    label: "Step 1 input text color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg1_input_placeholder_color",
    label: "Step 1 placeholder color",
    value: "rgba(34,34,34,0.7)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(34,34,34,0.7)",
  }),
];

/** Step 2 — background / employment */
const SECONDARY_INTAKE_FIELDS = [
  json({
    fieldName: "seg2_section_title",
    label:
      "Step indicator — Step 2 label (beside circle; shown only when Step 2 is active)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "Background",
  }),
  json({
    fieldName: "seg2_phase_hero_title",
    label: "Step 2 — Main headline (white card)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg2_phase_hero_subtitle",
    label: "Step 2 — Subtitle below headline",
    value: "",
    valueType: "string",
    filedType: "TEXT_AREA",
    placeholder:
      "Falls back to Step 1 subtitle, then phone-step hero subtitle, if unset.",
    required: false,
    defaultValue: "",
    rows: 2,
  }),
  json({
    fieldName: "seg2_phase_hero_title_color",
    label: "Step 2 — Headline color",
    value: "#111827",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#111827",
  }),
  json({
    fieldName: "seg2_phase_hero_subtitle_color",
    label: "Step 2 — Subtitle color",
    value: "#6B7280",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#6B7280",
  }),
  json({
    fieldName: "seg2_employment_prompt",
    label: "Working status toggle label",
    value: "",
    valueType: "string",
    filedType: "TEXT_AREA",
    placeholder: "e.g. Current Working status",
    required: false,
    defaultValue: "Current Working status",
    rows: 2,
  }),
  json({
    fieldName: "seg2_employment_prompt_color",
    label: "Working status label color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg2_working_toggle_active_background",
    label:
      "Working status — Selected “Yes” pill background (toggle active color, Figma)",
    value: "#342b7c",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#342b7c",
  }),
  json({
    fieldName: "seg2_working_toggle_inactive_border",
    label: 'Working status — Outline “No” pill border (inactive style)',
    value: "#c4c4c4",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#c4c4c4",
  }),
  json({
    fieldName: "seg2_qualification_year_label",
    label: "Highest qualification — year label",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Year of highest qualification",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg2_qualification_year_placeholder",
    label: "Highest qualification — year placeholder (dropdown)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "Select Year of highest qualification",
  }),
  json({
    fieldName: "seg2_qualification_year_minimum",
    label:
      "Highest qualification — minimum year shown in dropdown. Note: selectable years span from this year through the current calendar year (newest listed first). If you leave \"Year dropdown values\" empty below, the list is built automatically.",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Earliest selectable year",
    required: false,
    defaultValue: "2015",
  }),
  json({
    fieldName: "seg2_qualification_year_options",
    label: "Highest qualification — year dropdown values (optional)",
    value: "",
    valueType: "string",
    filedType: "TEXT_AREA",
    placeholder:
      "One year per line (e.g. 2024) or comma-separated. Leave empty to auto-fill years from the current calendar year down to the minimum year set above. If you specify years here, they are shown only when they fall between that minimum and the current calendar year.",
    required: false,
    defaultValue: "",
    rows: 6,
  }),
  json({
    fieldName: "seg2_state_label",
    label: "State — label (Indian states dropdown)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "State",
  }),
  json({
    fieldName: "seg2_state_label_color",
    label: "State — label color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg2_city_label",
    label: "City — label",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "City",
  }),
  json({
    fieldName: "seg2_city_label_color",
    label: "City — label color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg2_work_experience_label",
    label: "Total work experience — label",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "Total work experience",
  }),
  json({
    fieldName: "seg2_work_experience_label_color",
    label: "Total work experience — label color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg2_work_experience_placeholder",
    label: "Total work experience — placeholder",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "e.g. 3 Years",
  }),
  json({
    fieldName: "seg2_dob_label",
    label: "Date of Birth — label",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "Date of Birth",
  }),
  json({
    fieldName: "seg2_dob_label_color",
    label: "Date of Birth — label color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg2_dob_placeholder",
    label: "Date of Birth — placeholder / hint",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "DD/MM/YYYY",
  }),
  json({
    fieldName: "seg2_industry_label",
    label: "Industry label",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Industry",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg2_industry_label_color",
    label: "Industry label color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg2_company_label",
    label: "Company label",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Company name",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg2_company_label_color",
    label: "Company label color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg2_designation_label",
    label: "Designation label",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Designation / Role",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg2_designation_label_color",
    label: "Designation label color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg2_footer_consent_text",
    label:
      "Step 2 footer consent (checkbox above submit; falls back to Phone step consent if empty)",
    value: "",
    valueType: "string",
    filedType: "TEXT_AREA",
    placeholder: "",
    required: false,
    defaultValue: "",
    rows: 3,
  }),
  json({
    fieldName: "seg2_footer_consent_text_color",
    label: "Step 2 footer consent text color",
    value: "#272323",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#272323",
  }),
  json({
    fieldName: "seg2_submit_label",
    label: "Submit — button text",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Submit",
    required: false,
    defaultValue: "Submit",
  }),
  json({
    fieldName: "seg2_submit_button_text_color",
    label: "Submit — button text color",
    value: "#FFFFFF",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#FFFFFF",
  }),
  json({
    fieldName: "seg2_submit_button_background_color",
    label: "Submit — button background",
    value: "#02a7b6",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#02a7b6",
  }),
  json({
    fieldName: "seg2_input_border_color",
    label: "Step 2 input border color",
    value: "rgba(196,196,196,0.4)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(196,196,196,0.4)",
  }),
  json({
    fieldName: "seg2_input_background_color",
    label: "Step 2 input background",
    value: "rgba(196,196,196,0.2)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(196,196,196,0.2)",
  }),
  json({
    fieldName: "seg2_input_text_color",
    label: "Step 2 input text color",
    value: "#222222",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#222222",
  }),
  json({
    fieldName: "seg2_input_placeholder_color",
    label: "Step 2 placeholder color",
    value: "rgba(34,34,34,0.7)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(34,34,34,0.7)",
  }),
];

const COMPLETION_SEGMENT_FIELDS = [
  json({
    fieldName: "seg3_success_title",
    label: "Success title",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Thank You",
    required: false,
    defaultValue: "Thank You",
  }),
  json({
    fieldName: "seg3_success_title_color",
    label: "Success title color",
    value: "#FFF",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#FFF",
  }),
  json({
    fieldName: "seg3_success_subtitle",
    label: "Success subtitle",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg3_success_subtitle_color",
    label: "Success subtitle color",
    value: "rgba(255, 255, 255, 0.8)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(255, 255, 255, 0.8)",
  }),
  json({
    fieldName: "seg3_success_description",
    label: "Success description",
    value: "",
    valueType: "string",
    filedType: "TEXT_AREA",
    placeholder: "e.g. People left to enroll",
    required: false,
    defaultValue: "",
    rows: 4,
  }),
  json({
    fieldName: "seg3_success_description_color",
    label: "Success description color",
    value: "rgba(255, 255, 255, 0.9)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(255, 255, 255, 0.9)",
  }),
  json({
    fieldName: "seg3_success_disclaimer",
    label: "Success disclaimer",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Last updated: 2min ago",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg3_success_disclaimer_color",
    label: "Success disclaimer color",
    value: "rgba(255, 255, 255, 0.9)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(255, 255, 255, 0.9)",
  }),
  json({
    fieldName: "seg3_success_highlight_text",
    label: "Success highlight text (every {{id}} is replaced with the user's lead queue number)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Your position in the queue is #{{id}}",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg3_success_highlight_color",
    label: "Success highlight color",
    value: "#FFFFFF",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#FFFFFF",
  }),
  json({
    fieldName: "seg3_gradient_start",
    label: "Success gradient start",
    value: "#03a4b5",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#03a4b5",
  }),
  json({
    fieldName: "seg3_gradient_end",
    label: "Success gradient end",
    value: "#342b7c",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#342b7c",
  }),
  json({
    fieldName: "seg3_success_button_text",
    label: "Success — button text",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Back to website",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg3_success_button_text_color",
    label: "Success — button text color",
    value: "#FFFFFF",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#FFFFFF",
  }),
  json({
    fieldName: "seg3_success_button_background_color",
    label: "Success — button background",
    value: "rgba(255,255,255,0.1)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(255,255,255,0.1)",
  }),
  json({
    fieldName: "seg3_success_button_url",
    label: "Success — button link (optional)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "https://… or /path — opens when the button is clicked (new tab for https)",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg3_success_background_image",
    label: "Success — bottom background image",
    value: "",
    valueType: "string",
    filedType: "FILE",
    placeholder: "Upload image (same as Lead Form 2 success)",
    required: false,
    defaultValue: "",
    isJsonField: true,
  }),
  json({
    fieldName: "seg3_success_background_image_alt",
    label: "Success — background image alt text",
    value: "Success section background",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Alt text for accessibility",
    required: false,
    defaultValue: "Success section background",
    isJsonField: true,
  }),
];

const RETURNING_SEGMENT_FIELDS = [
  json({
    fieldName: "seg4_title",
    label: "Returning user title",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "You're already registered",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg4_title_color",
    label: "Returning title color",
    value: "#FFF",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#FFF",
  }),
  json({
    fieldName: "seg4_subtitle",
    label: "Returning user subtitle",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg4_subtitle_color",
    label: "Returning subtitle color",
    value: "rgba(255, 255, 255, 0.8)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(255, 255, 255, 0.8)",
  }),
  json({
    fieldName: "seg4_description",
    label: "Returning user description",
    value: "",
    valueType: "string",
    filedType: "TEXT_AREA",
    placeholder: "",
    required: false,
    defaultValue: "",
    rows: 3,
  }),
  json({
    fieldName: "seg4_description_color",
    label: "Returning description color",
    value: "rgba(255, 255, 255, 0.9)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(255, 255, 255, 0.9)",
  }),
  json({
    fieldName: "seg4_disclaimer",
    label: "Returning disclaimer",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Short line above highlight (e.g. queue position)",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg4_disclaimer_color",
    label: "Returning disclaimer color",
    value: "rgba(255, 255, 255, 0.8)",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "rgba(255, 255, 255, 0.8)",
  }),
  json({
    fieldName: "seg4_highlight_text",
    label: "Returning highlight text (every {{id}} is replaced with the user's lead queue number)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Your position in the queue is #{{id}}",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg4_highlight_color",
    label: "Returning highlight color",
    value: "#FFFFFF",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#FFFFFF",
  }),
  json({
    fieldName: "seg4_button_text",
    label: "Returning CTA label",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "OK",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg4_button_text_color",
    label: "Returning CTA text color",
    value: "#FFFFFF",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#FFFFFF",
  }),
  json({
    fieldName: "seg4_button_background_color",
    label: "Returning CTA background",
    value: "#02a7b6",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "#02a7b6",
  }),
  json({
    fieldName: "seg4_button_url",
    label: "Returning — button link (optional)",
    value: "",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "https://… or /path — if set, opens instead of restarting the form",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg4_gradient_start",
    label: "Returning — screen gradient start (optional; use with gradient end for full-bleed card like Lead Popup 2)",
    value: "",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "Leave empty for white card",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg4_gradient_end",
    label: "Returning — screen gradient end",
    value: "",
    valueType: "string",
    filedType: "COLOR",
    placeholder: "",
    required: false,
    defaultValue: "",
  }),
  json({
    fieldName: "seg4_background_image",
    label: "Returning — bottom background image",
    value: "",
    valueType: "string",
    filedType: "FILE",
    placeholder: "Upload image (same as Lead Popup 2 existing user)",
    required: false,
    defaultValue: "",
    isJsonField: true,
  }),
  json({
    fieldName: "seg4_background_image_alt",
    label: "Returning — background image alt text",
    value: "Returning visitor section background",
    valueType: "string",
    filedType: "INPUT",
    placeholder: "Alt text",
    required: false,
    defaultValue: "Returning visitor section background",
    isJsonField: true,
  }),
];

export const LEAD_DROP_FORM_SEGMENT_WORKBENCH_SEGMENTS = [
  { id: "phone", label: "Phone & OTP", fields: PHONE_INTAKE_FIELDS },
  { id: "primary", label: "Step 1 · Details", fields: PRIMARY_INTAKE_FIELDS },
  { id: "secondary", label: "Step 2 · Background", fields: SECONDARY_INTAKE_FIELDS },
  { id: "completion", label: "Completion screen", fields: COMPLETION_SEGMENT_FIELDS },
  { id: "returning", label: "Returning visitor", fields: RETURNING_SEGMENT_FIELDS },
] as const;

export const LEAD_DROP_FORM_SEGMENT_MERGED_FIELDS = [
  ...LEAD_STRIP_GLOBAL_FIELDS,
  ...MOBILE_STRIP_LEADDROP_CTA_FIELDS,
  ...LEAD_DROP_FORM_SEGMENT_WORKBENCH_SEGMENTS.flatMap((s) => s.fields),
];

function buildDefaultRow() {
  const row: Record<string, string> = {};
  for (const f of LEAD_DROP_FORM_SEGMENT_MERGED_FIELDS) {
    const field = f as { fieldName?: string; defaultValue?: string };
    if (field.fieldName) row[field.fieldName] = field.defaultValue ?? "";
  }
  return row;
}

export const LEAD_DROP_FORM_SEGMENTED_6_CONSTANTS = {
  DEFAULT_ROW: buildDefaultRow(),
  SEGMENT_WORKBENCH_SEGMENTS: LEAD_DROP_FORM_SEGMENT_WORKBENCH_SEGMENTS,
  MERGED_FIELDS_CONFIG: LEAD_DROP_FORM_SEGMENT_MERGED_FIELDS,
  LEAD_STRIP_GLOBAL_FIELDS,
  MOBILE_STRIP_LEADDROP_CTA_FIELDS,
  LEADDROP_MOBILE_STRIP_HELPER_TEXT,
  METADATA_FIELDS: [] as [],
};
